import React from 'react';
import { Shield, Target, Users, Award, Eye, Zap } from 'lucide-react';

const About = () => {
  const principles = [
    {
      icon: <Eye className="h-8 w-8 text-burnt-orange" />,
      title: "Operational Intelligence",
      description: "We see what others miss. While competitors focus on tasks, we architect systems that eliminate the need for tasks entirely."
    },
    {
      icon: <Shield className="h-8 w-8 text-burnt-orange" />,
      title: "Discretion as Standard",
      description: "Your operations remain confidential. We operate in the shadows, leaving only results in the light."
    },
    {
      icon: <Zap className="h-8 w-8 text-burnt-orange" />,
      title: "Force Multiplication",
      description: "We don't just complete tasks—we amplify your decision-making power and extend your operational reach."
    }
  ];

  const credentials = [
    {
      icon: <Award className="h-6 w-6 text-burnt-orange" />,
      title: "Elite Training",
      description: "Military precision meets corporate excellence"
    },
    {
      icon: <Users className="h-6 w-6 text-burnt-orange" />,
      title: "C-Level Experience",
      description: "We understand executive priorities because we've operated at that level"
    },
    {
      icon: <Target className="h-6 w-6 text-burnt-orange" />,
      title: "Proven Systems",
      description: "Battle-tested methodologies refined across industries"
    }
  ];

  return (
    <section id="about" className="section-padding bg-white">
      <div className="container-center">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-20 animate-slide-up">
          <div className="inline-flex items-center space-x-2 bg-burnt-orange/10 px-4 py-2 rounded-full">
            <Shield className="h-4 w-4 text-burnt-orange" />
            <span className="text-sm font-semibold text-burnt-orange">Command Structure</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-olive-brown">
            Executive Operations,{' '}
            <span className="text-gradient">Redefined</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-olive-brown/70 max-w-3xl mx-auto">
            FractionalVA isn't another virtual assistant service. We are operational architects
            who understand that true power lies not in doing more, but in controlling what deserves your attention.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-gray-light rounded-3xl p-6 sm:p-8 md:p-12 mb-20 animate-slide-up">
          <div className="text-center space-y-8">
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-olive-brown">
              Our Mission
            </h3>
            <blockquote className="text-base sm:text-lg md:text-xl lg:text-2xl font-medium text-olive-brown/80 leading-relaxed max-w-4xl mx-auto">
              "To eliminate the operational friction that dulls executive edge.
              We create the conditions where vision becomes reality,
              not where vision gets lost in logistics."
            </blockquote>
            <div className="w-24 h-1 bg-burnt-orange rounded-full mx-auto"></div>
          </div>
        </div>

        {/* Core Principles */}
        <div className="mb-20">
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-olive-brown text-center mb-12 animate-slide-up">
            Core Principles
          </h3>
          <div className="grid lg:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <div
                key={index}
                className="group bg-gray-light rounded-3xl p-6 sm:p-8 card-shadow hover:bg-gray-medium transition-all duration-500 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="space-y-6">
                  <div className="p-4 bg-white rounded-2xl w-fit group-hover:scale-110 transition-transform duration-300">
                    {principle.icon}
                  </div>
                  <h4 className="font-display text-lg sm:text-xl font-bold text-olive-brown">
                    {principle.title}
                  </h4>
                  <p className="text-sm sm:text-base text-olive-brown/70 leading-relaxed">
                    {principle.description}
                  </p>
                  <div className="w-full h-1 bg-gradient-to-r from-burnt-orange to-olive-brown rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credentials */}
        <div className="bg-gradient-to-r from-olive-brown to-burnt-orange rounded-3xl p-6 sm:p-8 md:p-12 mb-20 animate-slide-up">
          <div className="text-center space-y-8 text-white">
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold">
              Operational Credentials
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {credentials.map((credential, index) => (
                <div key={index} className="space-y-4">
                  <div className="inline-flex p-3 bg-white/20 rounded-full">
                    {credential.icon}
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold">
                    {credential.title}
                  </h4>
                  <p className="text-xs sm:text-sm opacity-90">
                    {credential.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Philosophy */}
        <div className="text-center space-y-8 animate-slide-up">
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-olive-brown">
            The FractionalVA Difference
          </h3>
          <div className="max-w-4xl mx-auto space-y-6 text-sm sm:text-base md:text-lg text-olive-brown/70 leading-relaxed">
            <p>
              Most virtual assistants manage your tasks. We eliminate the need for those tasks to exist.
              While others react to chaos, we architect the conditions that prevent chaos from forming.
            </p>
            <p>
              We understand that executive time isn't measured in hours—it's measured in decisions made,
              opportunities seized, and markets moved. Our role is to ensure that every moment of your attention
              is applied where only you can create value.
            </p>
            <p className="font-semibold text-burnt-orange">
              We don't just free up your time. We sharpen your power.
            </p>
          </div>

          <div className="pt-8">
            <a
              href="#contact"
              className="btn-primary"
              aria-label="Experience the FractionalVA difference"
            >
              Experience the Difference
            </a>
          </div>
        </div>

        {/* Confidential Notice */}
        <div className="mt-16 text-center bg-olive-brown/5 rounded-2xl p-8 animate-slide-up">
          <div className="space-y-4">
            <div className="inline-flex items-center space-x-2 bg-burnt-orange/10 px-4 py-2 rounded-full">
              <Shield className="h-4 w-4 text-burnt-orange" />
              <span className="text-sm font-semibold text-burnt-orange">CLASSIFIED</span>
            </div>
            <p className="text-olive-brown/70 italic">
              Operational details and client methodologies remain confidential. 
              What you see are results, not methods.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;